import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {
  TooltipCoordinator,
  TooltipCoordinatorProps,
} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ThemeContextType} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LensModel} from '../../../../../models/api/lensModel';
import {navigationLineChevronWidth} from '../../../../common/navigation/navigationLine';
import {LensValidation} from '../helpers/useLensValidation';
import {LensesWorkspaceErrorTooltip} from './lensesWorkspaceErrorTooltip';
import {LensesWorkspaceIngredientsTooltipContainer} from './lensesWorkspaceIngredientsTooltipContainer';

const lensesWorkspaceTooltipDefaultDelay = 100;

/*
 * Props.
 */

export interface LensesWorkspaceTooltipCoordinatorProps
  extends Omit<
    TooltipCoordinatorProps,
    'render' | 'persistent' | 'isMultiline' | 'minWidth' | 'maxWidth' | keyof ThemeContextType
  > {
  lens: LensModel;
  width?: number;
  lensValidation: LensValidation;
  rightItemMode?: boolean;
  isEditingWorkspace?: boolean;
}

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: grid;
  grid-column-start: chevron-start;
  grid-column-end: 5;
  grid-template-areas: 'chevron icon . label . right';
  grid-template-columns: ${navigationLineChevronWidth}px 22px 4px 1fr 4px;
  width: 100%;
`;

const StyledRightItemTooltipCoordinator = styled(TooltipCoordinator)`
  grid-area: right;
  align-self: center;
  min-width: 20px;
  display: flex;
  justify-content: center;
`;

/*
 * Component.
 */

export const LensesWorkspaceTooltipCoordinator: FC<
  PropsWithChildren<LensesWorkspaceTooltipCoordinatorProps>
> = ({lens, lensValidation, isEditingWorkspace, rightItemMode, width, ...tooltipProps}) => {
  const renderTooltip = () => {
    if (lensValidation.isLensInvalid) {
      return <LensesWorkspaceErrorTooltip lens={lens} lensValidation={lensValidation} />;
    }

    return <LensesWorkspaceIngredientsTooltipContainer lens={lens} />;
  };

  if (rightItemMode) {
    return (
      <StyledRightItemTooltipCoordinator
        placement="right-start"
        maxWidth={width}
        minWidth={width}
        customMargin="-7px auto auto 7px"
        render={renderTooltip}
        hideOnMouseDown={false}
        delay={lensesWorkspaceTooltipDefaultDelay}
        persistent={lensValidation.isLensInvalid}
        {...tooltipProps}
      />
    );
  }

  return (
    <StyledTooltipCoordinator
      placement="right-start"
      maxWidth={width}
      minWidth={width}
      customMargin="-4px auto auto 31px"
      render={isEditingWorkspace ? undefined : renderTooltip}
      hideOnMouseDown={false}
      delay={lensesWorkspaceTooltipDefaultDelay}
      persistent={lensValidation.isLensInvalid}
      {...tooltipProps}
    />
  );
};
